import React, { lazy, Suspense, useEffect, useState, useMemo } from 'react';
import { motion, AnimatePresence, useScroll, useTransform, useSpring } from 'framer-motion';
import { LanguageProvider, useLanguage } from '../contexts/LanguageContext';
import { Globe } from 'lucide-react';

// Lazy load heavy components for React Fiber optimization
const NajaHero = lazy(() => import('../components/website/sections/NajaHero'));
const ProblemSection = lazy(() => import('../components/website/sections/ProblemSection'));
const SolutionSection = lazy(() => import('../components/website/sections/SolutionSection'));
const ZelliAngelSection = lazy(() => import('../components/website/sections/ZelliAngelSection'));
const CheckpointsSection = lazy(() => import('../components/website/sections/CheckpointsSection'));
const TechStackSection = lazy(() => import('../components/website/sections/TechStackSection'));
const MarketSection = lazy(() => import('../components/website/sections/MarketSection'));
const CompetitionSection = lazy(() => import('../components/website/sections/CompetitionSection'));
const TeamSection = lazy(() => import('../components/website/sections/TeamSection'));
const ContactSection = lazy(() => import('../components/website/sections/ContactSection'));

// Loading fallback with smooth animation
// Loading fallback with smooth animation
const SectionLoader = () => {
    const { t } = useLanguage();
    return (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/90 backdrop-blur-xl">
            <motion.div
                className="w-16 h-16 border-t-2 border-r-2 border-cyan-500 rounded-full"
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            />
            <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="mt-4 text-cyan-400 text-xs font-bold tracking-[0.2em] animate-pulse"
            >
                {t('loader.text')}
            </motion.p>
        </div>
    );
};

// Navigation Component
const Navigation: React.FC = () => {
    const { t, language, setLanguage } = useLanguage();
    const [scrolled, setScrolled] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 50);
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const navItems = [
        { label: t('nav.problem'), href: '#problem' },
        { label: t('nav.solution'), href: '#solution' },
        { label: t('nav.zelliAngel'), href: '#zelli-angel' },
        { label: t('nav.technology'), href: '#technology' },
        { label: t('nav.market'), href: '#market' },
        { label: t('nav.team'), href: '#team' },
    ];

    return (
        <motion.nav
            initial={{ y: -100 }}
            animate={{ y: 0 }}
            className={`fixed top-0 w-full z-50 transition-all duration-500 ${scrolled
                ? 'bg-black/95 backdrop-blur-xl border-b border-white/5'
                : 'bg-transparent'
                }`}
        >
            <div className="container mx-auto px-4 sm:px-6 py-4 flex justify-between items-center">
                {/* Logo */}
                <motion.a
                    href="#"
                    className="flex items-center gap-3 group"
                    whileHover={{ scale: 1.02 }}
                >
                    <div className="relative">
                        <div className="w-10 h-10 bg-gradient-to-br from-cyan-400 via-cyan-500 to-blue-600 rounded-xl flex items-center justify-center font-black text-black text-sm shadow-lg shadow-cyan-500/30">
                            N
                        </div>
                        <motion.div
                            className="absolute inset-0 rounded-xl bg-cyan-400/50 blur-xl"
                            animate={{ opacity: [0.3, 0.6, 0.3], scale: [1, 1.2, 1] }}
                            transition={{ duration: 2, repeat: Infinity }}
                        />
                    </div>
                    <div className="flex flex-col">
                        <span className="text-sm font-bold tracking-widest text-white">NAJA AI</span>
                        <span className="text-[9px] text-cyan-400/70 tracking-wider">NATIONAL SAFETY GRID</span>
                    </div>
                </motion.a>

                {/* Desktop Navigation */}
                <div className="hidden lg:flex items-center gap-8">
                    {navItems.map((item, i) => (
                        <motion.a
                            key={item.label}
                            href={item.href}
                            className="text-xs tracking-widest text-white/60 hover:text-cyan-400 transition-colors relative group font-medium"
                            initial={{ opacity: 0, y: -20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 * i }}
                        >
                            {item.label}
                            <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-cyan-400 group-hover:w-full transition-all duration-300" />
                        </motion.a>
                    ))}
                </div>

                {/* CTA Button */}
                <div className="hidden md:flex items-center gap-4">
                    <motion.a
                        href="/pitch-deck"
                        className="px-4 py-2 text-xs font-bold tracking-widest text-cyan-400 border border-cyan-500/30 rounded-lg hover:bg-cyan-500/10 transition-all"
                        whileHover={{ scale: 1.02 }}
                    >
                        PITCH DECK
                    </motion.a>
                    <motion.a
                        href="mailto:aymane@zelli.space"
                        className="px-5 py-2.5 bg-gradient-to-r from-cyan-500 to-cyan-400 text-black text-xs font-bold tracking-widest rounded-lg shadow-lg shadow-cyan-500/30 hover:shadow-cyan-500/50 transition-all"
                        whileHover={{ scale: 1.02, y: -1 }}
                        whileTap={{ scale: 0.98 }}
                    >
                        {t('nav.investNow')}
                    </motion.a>

                    {/* Language Switcher */}
                    <button
                        onClick={() => setLanguage(language === 'en' ? 'ar' : 'en')}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-xs font-medium text-white/80 transition-all border border-white/10 hover:border-cyan-500/30"
                    >
                        <Globe className="w-3.5 h-3.5 text-cyan-400" />
                        <span className="font-mono">{language === 'en' ? 'AR' : 'EN'}</span>
                    </button>
                </div>

                {/* Mobile Menu Button */}
                <button
                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                    className="lg:hidden w-10 h-10 flex flex-col items-center justify-center gap-1.5"
                >
                    <motion.span
                        animate={{ rotate: mobileMenuOpen ? 45 : 0, y: mobileMenuOpen ? 6 : 0 }}
                        className="w-6 h-0.5 bg-white rounded-full"
                    />
                    <motion.span
                        animate={{ opacity: mobileMenuOpen ? 0 : 1 }}
                        className="w-6 h-0.5 bg-white rounded-full"
                    />
                    <motion.span
                        animate={{ rotate: mobileMenuOpen ? -45 : 0, y: mobileMenuOpen ? -6 : 0 }}
                        className="w-6 h-0.5 bg-white rounded-full"
                    />
                </button>
            </div>

            {/* Mobile Menu */}
            <AnimatePresence>
                {mobileMenuOpen && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="lg:hidden bg-black/95 backdrop-blur-xl border-t border-white/5"
                    >
                        <div className="container mx-auto px-4 py-6 flex flex-col gap-4">
                            {navItems.map((item) => (
                                <a
                                    key={item.label}
                                    href={item.href}
                                    onClick={() => setMobileMenuOpen(false)}
                                    className="text-sm text-white/70 hover:text-cyan-400 py-2"
                                >
                                    {item.label}
                                </a>
                            ))}
                            <a
                                href="mailto:aymane@zelli.space"
                                className="mt-4 px-5 py-3 bg-gradient-to-r from-cyan-500 to-cyan-400 text-black text-sm font-bold text-center rounded-lg"
                            >
                                {t('nav.investNow')}
                            </a>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.nav>
    );
};

// Progress indicator
const ScrollProgress: React.FC = () => {
    const { scrollYProgress } = useScroll();
    const scaleX = useSpring(scrollYProgress, { stiffness: 100, damping: 30 });

    return (
        <motion.div
            className="fixed top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-500 origin-left z-[60]"
            style={{ scaleX }}
        />
    );
};

const MarketingHomeContent: React.FC = () => {
    const { dir, t, isLoading } = useLanguage();
    return (
        <div className="min-h-screen bg-[#000000] text-white font-sans overflow-x-hidden selection:bg-cyan-500/30 selection:text-white" dir={dir}>
            <ScrollProgress />
            <Navigation />


            <AnimatePresence>
                {isLoading && <SectionLoader />}
            </AnimatePresence>

            <main>
                {/* Unified Suspense Boundary for Single Loader */}
                <Suspense fallback={<SectionLoader />}>
                    <NajaHero />
                    <ProblemSection />
                    <SolutionSection />
                    <ZelliAngelSection />
                    <CheckpointsSection />
                    <TechStackSection />
                    <MarketSection />
                    <CompetitionSection />
                    <TeamSection />
                    <ContactSection />
                </Suspense>
            </main>

            {/* Footer */}
            <footer className="py-8 border-t border-white/5 bg-black">
                <div className="container mx-auto px-4 sm:px-6">
                    <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-gradient-to-br from-cyan-400 to-cyan-600 rounded-lg flex items-center justify-center font-black text-black text-xs">
                                N
                            </div>
                            <span className="text-sm text-white/60">{t('footer.rights')}</span>
                        </div>
                        <div className="flex items-center gap-6 text-xs text-white/40">
                            <a href="mailto:aymane@zelli.space" className="hover:text-cyan-400 transition-colors">{t('footer.contact')}</a>
                            <a href="/pitch-deck" className="hover:text-cyan-400 transition-colors">{t('footer.pitchDeck')}</a>
                            <span>{t('footer.builtWith')}</span>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
};

const MarketingHome: React.FC = () => (
    <LanguageProvider>
        <MarketingHomeContent />
    </LanguageProvider>
);

export default MarketingHome;
